// Runtime config resolved from Vite build-time env vars.
//
// `VITE_HEZO_OPS_BASE_URL` — origin the SPA talks to. Defaults to the staging
// hostname; the prod build sets it to `https://ops.hezo-cycling.com`, the
// staging build sets it to `https://ops.hq.hezo-cycling.com`. Local dev
// typically points it at a tunnel or `http://localhost:3000`.
//
// `VITE_DEBUG` — "1" / "true" toggles the on-page JSON-debug `<details>`
// blocks that show raw request/response bodies. Off by default.

const RAW_BASE = (import.meta.env.VITE_HEZO_OPS_BASE_URL ?? "").trim();
const DEFAULT_BASE = "https://ops.hq.hezo-cycling.com";

export const HEZO_OPS_BASE_URL: string = (RAW_BASE.length > 0 ? RAW_BASE : DEFAULT_BASE).replace(
  /\/+$/,
  "",
);

const DEBUG_RAW = String(import.meta.env.VITE_DEBUG ?? "").toLowerCase();
export const DEBUG: boolean = DEBUG_RAW === "1" || DEBUG_RAW === "true";

// `VITE_DEV_CAPTURE` — "1" / "true" allows `?dev=1` to run the capture flow with
// NO invite, saving the result to a file instead of uploading (see
// `devCaptureEnabled`). Deliberately a separate flag from VITE_DEBUG: every
// build path currently ships VITE_DEBUG=0, and turning that on for staging would
// also unfold every raw-JSON debug block and force diag mode on, making staging
// stop resembling prod. This flag is set on the staging builds only; the prod
// workflow hardcodes it to 0, so a prod bundle cannot be talked into dev capture
// by any URL.
const DEV_CAPTURE_RAW = String(import.meta.env.VITE_DEV_CAPTURE ?? "").toLowerCase();
export const DEV_CAPTURE_BUILD: boolean =
  DEV_CAPTURE_RAW === "1" || DEV_CAPTURE_RAW === "true" || DEBUG;

// Build identity, shown in the diag panel so an on-device tester can tell which
// deploy they're looking at. CI passes VITE_APP_VERSION=<git sha> on every build
// path (deploy-staging / deploy / docker); local dev has none → "dev". Trimmed
// to the short SHA.
const VERSION_RAW = String(import.meta.env.VITE_APP_VERSION ?? "").trim();
export const APP_VERSION: string = VERSION_RAW ? VERSION_RAW.slice(0, 7) : "dev";

/**
 * Pull the JWT magic-link token from the current URL. hezo-ops mints links
 * shaped `https://scan.hezo-cycling.com/?t=<jwt>` (see
 * `hezo-ops/src/app/api/scan-invites/route.ts`), so we look at `?t=` first.
 * Falls back to a `/scan/<jwt>` path segment for forward-compatibility with
 * any future URL shape.
 */
export function tokenFromLocation(loc: Location = window.location): string | null {
  const q = new URLSearchParams(loc.search);
  const t = q.get("t");
  if (t && t.length > 0) return t;
  const m = loc.pathname.match(/^\/scan\/([^/]+)/);
  if (m) return decodeURIComponent(m[1]);
  return null;
}

/**
 * Run the capture flow with NO invite (`?dev=1`), for on-device debugging.
 *
 * Normally the JWT magic-link IS the auth: no token → landing screen. That makes
 * every device test depend on minting an invite in hezo-ops first, which is
 * friction for things that have nothing to do with the session — camera
 * behaviour, guidance thresholds, floor telemetry. In dev capture the SPA
 * synthesises a session, runs the real capture UI, and saves the result to a
 * file instead of uploading (there are no presigned URLs without a JWT, and
 * inventing a fake upload would prove nothing).
 *
 * Gated on `DEV_CAPTURE_BUILD` as well as the URL, so this cannot be reached on
 * a production bundle — the bypass exists only where it is built in.
 */
export function devCaptureEnabled(loc: Location = window.location): boolean {
  return DEV_CAPTURE_BUILD && new URLSearchParams(loc.search).get("dev") === "1";
}

/**
 * The stand-in session for dev capture. `scanID` keeps hezo-ops's documented
 * `[A-Z0-9]{6}` shape so nothing downstream has to special-case it, and the
 * customerId is obviously non-real: these bytes never reach S3.
 */
export const DEV_SESSION = {
  customerId: "dev-no-invite",
  scanID: "DEV000",
  flow: "self-capture",
} as const;

// `VITE_CAPTURE_INGEST_URL` — where a dev capture is POSTed instead of being
// downloaded (see `captureIngestToken`). Points at the capture-ingest service on
// CT 111 (`tools/capture_ingest` in footscan-server), which spools the dump for
// the reconstruction worker. Overridable so a local ingest can be pointed at
// without a code change.
const INGEST_URL_RAW = String(import.meta.env.VITE_CAPTURE_INGEST_URL ?? "").trim();
export const CAPTURE_INGEST_URL: string =
  INGEST_URL_RAW.length > 0 ? INGEST_URL_RAW : "https://capture-ingest.rks26.de/ingest";

// Persisted capture-ingest token. Set once per device via `?ingest=<token>`;
// `?ingest=` (empty) or `?ingest=off` clears it again.
const INGEST_LS_KEY = "hezo.ingestToken";

/**
 * The capture-ingest token, or null for "download the file like before".
 *
 * A dev capture has no JWT and nowhere to PUT, so the bytes came down as a file
 * and were carried to a laptop by hand. With a token they go straight to the
 * ingest instead, which spools them for the reconstruction worker — the phone
 * never produces a file. The token is a shared secret for a service that runs
 * solvers on whatever it is handed, so it is opt-in per device and never built
 * into the bundle: pass it once as `?ingest=<token>`, it persists in
 * localStorage, and everything after that is unchanged from today.
 *
 * Gated on `DEV_CAPTURE_BUILD` like `devCaptureEnabled`, so a production bundle
 * cannot be talked into posting captures anywhere by any URL.
 */
export function captureIngestToken(loc: Location = window.location): string | null {
  if (!DEV_CAPTURE_BUILD) return null;
  const q = new URLSearchParams(loc.search).get("ingest");
  if (q !== null) {
    const clearing = q.length === 0 || q === "off";
    try {
      if (clearing) localStorage.removeItem(INGEST_LS_KEY);
      else localStorage.setItem(INGEST_LS_KEY, q);
    } catch {
      /* storage unavailable — the URL still governs this page load */
    }
    return clearing ? null : q;
  }
  try {
    const stored = localStorage.getItem(INGEST_LS_KEY);
    return stored && stored.length > 0 ? stored : null;
  } catch {
    return null;
  }
}

// Last capture label, remembered so the field comes up pre-filled. A capture
// session is usually several runs of the same foot, and re-typing the same note
// on a phone keyboard each time is how they end up inconsistent — which defeats
// the grouping the label exists for.
const LABEL_LS_KEY = "hezo.captureLabel";

/** The remembered capture label, or "" when there is none. */
export function storedCaptureLabel(): string {
  try {
    return localStorage.getItem(LABEL_LS_KEY) ?? "";
  } catch {
    return "";
  }
}

/** Remember (or, for an empty value, forget) the capture label. */
export function storeCaptureLabel(value: string): void {
  try {
    if (value.length > 0) localStorage.setItem(LABEL_LS_KEY, value);
    else localStorage.removeItem(LABEL_LS_KEY);
  } catch {
    /* storage unavailable — the label still applies to this run */
  }
}

/**
 * Escape hatch to turn off the on-device ONNX capture-guidance (mirrors the
 * prototype's `?ai=none`). Handy on low-end devices or when debugging the plain
 * manual-capture path — the flow works fine either way.
 */
export function guidanceDisabledByUrl(loc: Location = window.location): boolean {
  return new URLSearchParams(loc.search).get("ai") === "none";
}

/**
 * Debug override for the ONNX execution provider (`?ep=wasm` or `?ep=webgpu`).
 * Lets us A/B WebGPU vs. the wasm fallback on a real device without a rebuild —
 * WebGPU on iOS Safari is a prime suspect for the capture-page tab crashes.
 * Returns null (→ default `webgpu`-then-`wasm`) when unset or unrecognised.
 */
export function guidanceEpOverride(
  loc: Location = window.location,
): "wasm" | "webgpu" | null {
  const ep = new URLSearchParams(loc.search).get("ep");
  return ep === "wasm" || ep === "webgpu" ? ep : null;
}

/**
 * Override for the camera track request (`?track=`), for on-device checks of
 * other resolutions. Every scan asks for the full 4032×3024 sensor frame by
 * default (see `captureVideoSize`). `720` is the former 1280×720 default, `43`
 * asks for the 4:3 aspect only, `960`, `1440`, `1920` for 960×720, 1440×1080
 * and 1920×1440. Returns null when unset or unrecognised.
 */
export type TrackPreset = "720" | "43" | "960" | "1440" | "1920" | "4032";

const TRACK_PRESET_VALUES: readonly TrackPreset[] = ["720", "43", "960", "1440", "1920", "4032"];

export function trackPresetOverride(loc: Location = window.location): TrackPreset | null {
  const t = new URLSearchParams(loc.search).get("track");
  return TRACK_PRESET_VALUES.find((p) => p === t) ?? null;
}

// Persisted diagnostics flag. Lets on-device testers flip diag mode without a
// cable/URL param — set once (via the hidden logo gesture, see CaptureFlow) and
// it survives reloads. Namespaced so it can't collide with other app storage.
const DIAG_LS_KEY = "hezo.diag";

/**
 * Verbose capture diagnostics: console logging (selected EP, frame heartbeat,
 * swallowed frame errors) AND the on-screen camera-diag panel. On in DEBUG
 * builds, via `?diag=1`, or once toggled on-device (persisted in localStorage),
 * so a customer-facing prod build stays silent by default.
 */
export function diagEnabled(loc: Location = window.location): boolean {
  if (DEBUG) return true;
  if (new URLSearchParams(loc.search).get("diag") === "1") return true;
  try {
    return localStorage.getItem(DIAG_LS_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Persist the diagnostics toggle. Backs the on-device diag switch so a tester
 * can enable it with a gesture (no cable, no URL param) and keep it across
 * reloads. Best-effort: a storage failure (private mode / disabled) is ignored.
 */
export function setDiagEnabled(on: boolean): void {
  try {
    if (on) localStorage.setItem(DIAG_LS_KEY, "1");
    else localStorage.removeItem(DIAG_LS_KEY);
  } catch {
    /* storage unavailable — diag stays whatever the URL/DEBUG says */
  }
}

/**
 * iOS / iPadOS detection. Every browser on iOS runs on WebKit, whose WebGPU
 * implementation crashes the tab under the live-guidance loop (confirmed on
 * iPhone Safari: `?ep=webgpu` reliably kills the tab, `?ep=wasm` is stable).
 * iPadOS 13+ masquerades as desktop "MacIntel", so the touch-point check catches
 * it. Used to steer iOS onto the wasm execution provider.
 */
export function isIOS(nav: Navigator = navigator): boolean {
  const ua = nav.userAgent || "";
  const platform = nav.platform || "";
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    /iPad|iPhone|iPod/.test(platform) ||
    (platform === "MacIntel" && nav.maxTouchPoints > 1)
  );
}

/**
 * Which ONNX execution providers to request for the guidance session. An
 * explicit `?ep=` override wins (debug lever); otherwise iOS gets wasm-only
 * (WebGPU crashes the tab there) and every other platform keeps the
 * WebGPU→wasm preference (fast where WebGPU is stable, wasm fallback otherwise).
 */
export function guidanceProviders(
  loc: Location = window.location,
  nav: Navigator = navigator,
): Array<"webgpu" | "wasm"> {
  const override = guidanceEpOverride(loc);
  if (override) return [override];
  return isIOS(nav) ? ["wasm"] : ["webgpu", "wasm"];
}
